import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { deleteDkimKey, getDkimKey, verifyDkimKey } from 'src/actions/dkimKeys';
import { showAlert } from 'src/actions/globalAlert';
import { get as getSendingDomain } from 'src/actions/sendingDomains';
import { CopyField, DeleteModal, LabelledValue, Loading } from 'src/components';
import { PageLink } from 'src/components/links';
// prettier-ignore
import { Button, Grid, Layout, Page, Panel, Stack, Tag, Text } from 'src/components/matchbox';
import useModal from 'src/hooks/useModal';
import { DETAILS_BASE_URL } from '../constants';
import Domains from './index';

function StatusTag({ status }) {
  switch (status) {
    case 'valid':
      return <Tag color="green">Valid</Tag>;
    case 'invalid':
      return <Tag color="red">Invalid</Tag>;
    case 'unverified':
      return <Tag color="red">Unverified</Tag>;
    case 'pending':
      return <Tag>Pending</Tag>;
    default:
      return <Tag>{status}</Tag>;
  }
}

function DkimKeyDetailsPage({
  domain,
  domainLoading,
  domainError,
  dkimKey,
  getLoading,
  getError,
  verifyLoading,
  deleteLoading,
  getSendingDomain,
  getDkimKey,
  verifyDkimKey,
  deleteDkimKey,
  showAlert
}) {
  const history = useHistory();
  const { id: domainId, keyId } = useParams();
  const subaccountId = domain?.subaccount_id;
  const { closeModal, isModalOpen, openModal } = useModal();

  const dkimKeysPath = `${DETAILS_BASE_URL}/sending-bounce/${domainId}/dkim-keys`;
  const lastFetchedDomainId = useRef(null);

  // Load domain data if not loaded or if stale (needed for correct subaccount_id)
  // Uses ref to track last fetch attempt, allowing retry after navigation even if previous domain errored
  useEffect(() => {
    if (
      domainId &&
      domain?.id !== domainId &&
      !domainLoading &&
      lastFetchedDomainId.current !== domainId
    ) {
      lastFetchedDomainId.current = domainId;
      getSendingDomain(domainId);
    }
  }, [getSendingDomain, domainId, domainLoading, domain.id]);

  // Only fetch DKIM key once the correct domain is loaded (to get correct subaccount_id)
  useEffect(() => {
    if (domainId && keyId && domain?.id === domainId) {
      getDkimKey({ domain: domainId, keyId, subaccount: subaccountId });
    }
  }, [getDkimKey, domainId, keyId, subaccountId, domain.id]);

  const handleVerify = () => {
    verifyDkimKey({ domain: domainId, keyId, subaccount: subaccountId }).then((result) => {
      if (result.dkim_status === 'valid') {
        showAlert({
          type: 'success',
          message: `DKIM key "${keyId}" has been verified successfully.`
        });
        getDkimKey({ domain: domainId, keyId, subaccount: subaccountId });
      } else if (result.dkim_status === 'pending') {
        showAlert({
          type: 'info',
          message: `DKIM key "${keyId}" verification is pending. DNS changes may take time to propagate.`
        });
        getDkimKey({ domain: domainId, keyId, subaccount: subaccountId });
      } else {
        showAlert({
          type: 'error',
          message: `Unable to verify DKIM key "${keyId}".`,
          details: result.dns?.dkim_error
        });
      }
    });
  };

  const handleDelete = () => {
    deleteDkimKey({ domain: domainId, keyId, subaccount: subaccountId })
      .then(() => {
        closeModal();
        showAlert({
          type: 'success',
          message: `DKIM key "${keyId}" has been deleted.`
        });
        history.push(dkimKeysPath);
      })
      .catch(() => {
        closeModal();
      });
  };

  if (domainLoading || getLoading) {
    return <Loading />;
  }

  if (domainError) {
    return (
      <Domains.Container>
        <Page title="DKIM Key Details">
          <Panel accent="red">
            <Panel.Section>
              <Text>Unable to load domain information.</Text>
            </Panel.Section>
          </Panel>
        </Page>
      </Domains.Container>
    );
  }

  if (getError) {
    return (
      <Domains.Container>
        <Page title="DKIM Key Details">
          <Panel accent="red">
            <Panel.Section>
              <Text>Unable to load DKIM key details.</Text>
            </Panel.Section>
          </Panel>
        </Page>
      </Domains.Container>
    );
  }

  if (!dkimKey) {
    return null;
  }

  const isVerified = dkimKey.dkim_status === 'valid';
  const isDefault = dkimKey.is_default;
  const dnsHelpText = isVerified
    ? 'This key has been verified. The DNS record below should remain in place.'
    : 'Add the following TXT record to your DNS provider, then click "Verify Key" to complete setup.';
  const defaultHostname = `${dkimKey.selector}._domainkey.${dkimKey.signing_domain}`;
  const dkimHostname = dkimKey.dns?.dkim_record || defaultHostname;
  const defaultValue = `v=DKIM1; k=rsa; h=sha256; p=${dkimKey.public}`;
  const dkimValue = dkimKey.dns?.dkim_value || defaultValue;
  const deleteConfirmText =
    `Are you sure you want to delete the DKIM key "${keyId}"? ` +
    'This action cannot be undone. Any transmissions specifically targeting this key ID will be rejected.';

  return (
    <Domains.Container>
      <Page
        title={`DKIM Key: ${keyId}`}
        breadcrumbAction={{
          content: 'DKIM Keys',
          as: PageLink,
          to: dkimKeysPath
        }}
        primaryAction={{
          content: 'Edit Key',
          as: PageLink,
          to: `${dkimKeysPath}/${keyId}/edit`
        }}
      >
        <Layout>
          <Layout.Section annotated>
            <Layout.SectionTitle>Key Information</Layout.SectionTitle>
          </Layout.Section>

          <Layout.Section>
            <Panel>
              <Panel.Section>
                <Grid>
                  <Grid.Column xs={12} md={6}>
                    <LabelledValue label="Key ID">
                      <Text>{dkimKey.id}</Text>
                    </LabelledValue>
                  </Grid.Column>
                  <Grid.Column xs={12} md={6}>
                    <LabelledValue label="Status">
                      <StatusTag status={dkimKey.dkim_status} />
                    </LabelledValue>
                  </Grid.Column>
                  <Grid.Column xs={12} md={6}>
                    <LabelledValue label="Selector">
                      <Text>{dkimKey.selector}</Text>
                    </LabelledValue>
                  </Grid.Column>
                  <Grid.Column xs={12} md={6}>
                    <LabelledValue label="Signing Domain">
                      <Text>{dkimKey.signing_domain}</Text>
                    </LabelledValue>
                  </Grid.Column>
                  <Grid.Column xs={12} md={6}>
                    <LabelledValue label="Headers">
                      <Text>{dkimKey.headers}</Text>
                    </LabelledValue>
                  </Grid.Column>
                  <Grid.Column xs={12} md={6}>
                    <LabelledValue label="Default Key">
                      <Text>{isDefault ? 'Yes' : 'No'}</Text>
                    </LabelledValue>
                  </Grid.Column>
                </Grid>
              </Panel.Section>
            </Panel>
          </Layout.Section>
        </Layout>

        <Layout>
          <Layout.Section annotated>
            <Layout.SectionTitle>DNS Configuration</Layout.SectionTitle>
            {/* eslint-disable local/restrict-translatable-text */}
            <Stack>
              <Text color="gray.700" fontSize="200">
                {dnsHelpText}
              </Text>
            </Stack>
            {/* eslint-enable local/restrict-translatable-text */}
          </Layout.Section>

          <Layout.Section>
            <Panel>
              <Panel.Section>
                <Stack>
                  {!isVerified && (
                    <>
                      <Text as="label" fontWeight="500" fontSize="200">
                        Type
                      </Text>
                      <Text as="p">TXT</Text>
                    </>
                  )}
                  <CopyField
                    id="dkim-hostname-copy-field"
                    label="Hostname"
                    value={dkimHostname}
                    hideCopy={isVerified}
                  />
                  <CopyField
                    id="dkim-value-copy-field"
                    label="Value"
                    value={dkimValue}
                    hideCopy={isVerified}
                  />
                </Stack>
              </Panel.Section>

              {!isVerified && (
                <Panel.Section>
                  <Button variant="primary" onClick={handleVerify} loading={verifyLoading}>
                    Verify Key
                  </Button>
                </Panel.Section>
              )}
            </Panel>
          </Layout.Section>
        </Layout>

        {dkimKey.public && (
          <Layout>
            <Layout.Section annotated>
              <Layout.SectionTitle>Public Key</Layout.SectionTitle>
            </Layout.Section>

            <Layout.Section>
              <Panel>
                <Panel.Section>
                  <CopyField
                    id="dkim-public-key-copy-field"
                    label="Public Key (Base64)"
                    value={dkimKey.public}
                  />
                </Panel.Section>
              </Panel>
            </Layout.Section>
          </Layout>
        )}

        {!isDefault && (
          <Layout>
            <Layout.Section annotated />
            <Layout.Section>
              <Button variant="destructive" onClick={openModal}>
                Delete Key
              </Button>
            </Layout.Section>
          </Layout>
        )}

        <DeleteModal
          open={isModalOpen}
          title="Delete DKIM Key"
          content={<p>{deleteConfirmText}</p>}
          onCancel={closeModal}
          onDelete={handleDelete}
          isPending={deleteLoading}
        />
      </Page>
    </Domains.Container>
  );
}

const mapStateToProps = (state) => ({
  domain: state.sendingDomains.domain,
  domainLoading: state.sendingDomains.getLoading,
  domainError: state.sendingDomains.getError,
  dkimKey: state.dkimKeys.key,
  getLoading: state.dkimKeys.getLoading,
  getError: state.dkimKeys.getError,
  verifyLoading: state.dkimKeys.verifyLoading,
  deleteLoading: state.dkimKeys.deleteLoading
});

export default connect(mapStateToProps, {
  getSendingDomain,
  getDkimKey,
  verifyDkimKey,
  deleteDkimKey,
  showAlert
})(DkimKeyDetailsPage);
